import { useI18n } from '@react/hooks';
import { AspectCard } from './AspectCard';
import type { AspectCardData } from '@/features/aspects/types';

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function LayersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  );
}

interface AspectPanelProps {
  aspects: AspectCardData[];
  isLoading: boolean;
  onDetach: (aspectId: string) => void;
  onNavigate: (data: AspectCardData) => void;
  onAddClick: () => void;
}

export function AspectPanel({ aspects, isLoading, onDetach, onNavigate, onAddClick }: AspectPanelProps) {
  const { t } = useI18n();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-rail-foreground">
          {t('layout.rail.aspects')}
          {aspects.length > 0 && (
            <span className="ml-1.5 text-xs text-muted-foreground">({aspects.length})</span>
          )}
        </h3>
        <button
          onClick={onAddClick}
          className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          {t('rail.aspects.addCapability')}
        </button>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="animate-pulse p-3 rounded-lg border border-rail-border">
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 bg-muted rounded-md" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && aspects.length === 0 && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <LayersIcon className="h-8 w-8 text-muted-foreground/50 mb-2" />
          <p className="text-sm font-medium text-muted-foreground">{t('rail.aspects.emptyTitle')}</p>
          <p className="text-xs text-muted-foreground/70 mt-1 max-w-[200px]">
            {t('rail.aspects.emptyDescription')}
          </p>
          <button
            onClick={onAddClick}
            className="mt-3 text-xs text-primary hover:underline"
          >
            {t('rail.aspects.addCapability')}
          </button>
        </div>
      )}

      {!isLoading && aspects.length > 0 && (
        <div className="space-y-2">
          {aspects.map((aspect) => (
            <AspectCard
              key={aspect.id}
              data={aspect}
              onDetach={onDetach}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}
